import { Tag, TagGroup } from '../../../api/core'
import { hiddenLiveEventTagGroups } from '../../../features/live-events/types/LiveEventTagGroup'

export interface EventTypeOption {
  id: string
  name: string
  categoryId?: string
  categoryName: string
}

/**
 * Builds one option per live event type, ordered by event category and then by name inside the
 * category, so the grouped dropdown renders one heading per category.
 *
 * The event categories are the source of the list, which leaves out the tags that belong to no
 * category and the ones in a group that is hidden from the live events filters.
 */
export const buildEventTypeOptions = (tagGroups: TagGroup[] | undefined, tags: Tag[] | undefined): EventTypeOption[] => {
  if (!tagGroups || !tags) {
    return []
  }

  const tagsById = new Map(tags.map((tag) => [tag.id, tag]))
  const seenTypeIds = new Set<string>()

  return [...tagGroups]
    .filter((tagGroup) => !hiddenLiveEventTagGroups.includes(tagGroup.id))
    .sort((a, b) => a.order - b.order)
    .flatMap((tagGroup) =>
      tagGroup.tags
        .map((typeId) => tagsById.get(typeId))
        .filter((tag): tag is Tag => !!tag && tag.active && !seenTypeIds.has(tag.id))
        .map((tag) => {
          seenTypeIds.add(tag.id)
          return { id: tag.id, name: tag.name || tag.id, categoryId: tagGroup.id, categoryName: tagGroup.name }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    )
}

/**
 * Resolves selected event type ids into options, in the order of the dropdown.
 *
 * An id that is no longer in the tag list is kept as an option of its own so that the event type
 * overhaul retiring a type does not silently drop it from a saved selection.
 */
export const resolveEventTypeSelection = (
  selectedTypeIds: string[],
  options: EventTypeOption[],
  { unknownCategoryLabel }: { unknownCategoryLabel: string }
): EventTypeOption[] => {
  const uniqueSelectedTypeIds = [...new Set(selectedTypeIds)]
  const selectedOptions = options.filter((option) => uniqueSelectedTypeIds.includes(option.id))
  const unknownTypeIds = uniqueSelectedTypeIds.filter((typeId) => !options.some((option) => option.id === typeId))

  return [...selectedOptions, ...unknownTypeIds.map((typeId) => ({ id: typeId, name: typeId, categoryName: unknownCategoryLabel }))]
}
